import cn from 'classnames';
import { FC, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel } from '@/entities/reports';
import { userModel } from '@/entities/user';

import { useRemoveReportSignature } from '../../hooks';
import { AddSignature } from '../AddSignature/AddSignature';
import { ReportSigns } from '../ReportSigns/ReportSigns';

import styles from './ReportSignatures.module.scss';

interface ReportSignaturesProps {
  className?: string;
}

export const ReportSignatures: FC<ReportSignaturesProps> = ({ className }) => {
  const [loading, setLoading] = useState(false);

  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const user = useAppSelector(userModel.selectors.selectCurrentUser);
  const dispatch = useAppDispatch();

  const reportID = report?.ID || '';

  const canAddSign =
    report?.Signature?.UserSignatures.length &&
    !report?.Signature.UserSignatures.find(
      (userSignature) => userSignature.UserID === user.ID,
    ) &&
    report.YourPermissions?.CanSign;

  const canRemoveSign = Boolean(report?.YourPermissions?.CanSign);

  const removeReportSignature = useRemoveReportSignature(reportID);

  const handleSign = async () => {
    if (!reportID) return;

    setLoading(true);

    try {
      const response = await dispatch(
        reportsModel.thunks.signReport({ ReportID: reportID }),
      ).unwrap();

      if (response.Report) {
        dispatch(reportsModel.actions.setNewestOne(response.Report));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!report?.Signature || report?.Signature.UserSignatures.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-section="report-signatures"
    >
      <ReportSigns
        signedUsers={report.Signature.UserSignatures}
        onRemoveSignature={canRemoveSign ? removeReportSignature : undefined}
      />

      {canAddSign && (
        <AddSignature user={user} loading={loading} handleSign={handleSign} />
      )}
    </div>
  );
};
